import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { ReactComponent as PortalIcon } from "../icons/Portal.svg";
import { ToolbarButton } from "../input/ToolbarButton";
import { Sidebar } from "../sidebar/Sidebar";
import { PortalItem } from "./PortalItem";
import styles from "./PortalsSidebar.scss";
import { makeStyles, Skeleton, SkeletonItem, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
  skeletonCard: {
    width: "100%",
    maxWidth: "400px",
    minHeight: "200px",
    boxShadow: tokens.shadow2,
    borderRadius: tokens.borderRadiusMedium
  },
  skeletonImage: {
    width: "100%",
    height: "200px",
    marginBottom: "12px"
  },
  skeletonTitle: {
    width: "70%",
    height: "20px",
    marginBottom: "8px"
  },
  portalsList: {
    marginTop: "10px",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXL
  }
});

export function PortalsSidebar({ className = "", onClose, portals, loading }) {
  const styles = useStyles();

  const handlePortalClick = portalUrl => {
    window.location.href = portalUrl;
    onClose();
  };

  return (
    <Sidebar
      title={<FormattedMessage id="portals-sidebar.title" defaultMessage="Portals" />}
      className={className}
      onClose={onClose}
    >
      {loading ? (
        <div className={styles.portalsList}>
          {[...Array(4)].map((_, index) => (
            <div
              className={styles.skeletonCard}
              key={`portal-skeleton-${index}`}
              aria-busy="true"
              aria-label={<FormattedMessage id="portal.loading" defaultMessage="Loading portal..." />}
            >
              <Skeleton animation="wave">
                <SkeletonItem shape="rectangle" className={styles.skeletonImage} />
                <SkeletonItem shape="rectangle" className={styles.skeletonTitle} />
              </Skeleton>
            </div>
          ))}
        </div>
      ) : portals && portals.length ? (
        <>
          <p className={styles.portalsInstructions}>
            <FormattedMessage id="portals-sidebar.instructions" defaultMessage="Select the Verse you want to jump to" />
          </p>
          <div className={styles.portalsList}>
            {portals.map((portalUrl, i) => (
              <PortalItem
                onClick={() => handlePortalClick(portalUrl)}
                portalUrl={portalUrl}
                key={`${portalUrl}-${i}`}
              />
            ))}
          </div>
        </>
      ) : (
        <div className={styles.noPortals}>
          <FormattedMessage id="portals-sidebar.noPortals" defaultMessage="No portals available" />
        </div>
      )}
    </Sidebar>
  );
}

export function PortalsToolbarButton(props) {
  return (
    <ToolbarButton
      {...props}
      icon={<PortalIcon />}
      preset="accent4"
      label={<FormattedMessage id="portal-toolbar-button" defaultMessage="Portals" />}
    />
  );
}

PortalsSidebar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  portals: PropTypes.array,
  loading: PropTypes.bool // Add loading prop type
};

PortalsSidebar.defaultProps = {
  portals: [],
  loading: false // Set default loading to false
};
